<template>
  <VModal ref="dialog" disable-backdrop-click @close="$emit('close')">
    <template #default="modalProps">
      <div class="rounded shadow-xs w-full max-w-full h-full mx-auto">
        <div class="rounded shadow-lg bg-gray-100 overflow-hidden h-full flex flex-col">
          <div class="px-4 sm:px-6 py-5 border-b flex-none">
            <div class="flex items-center justify-between gap-6">
              <div class="flex-1 -m-2">
                <BaseVeeForm v-if="emailCampaign" :action="updateEmailCampaignName">
                  <!-- formProps
                    isIdle, isPending, isResolved, isRejected, invalid
                    errors, submit
                  -->
                  <template #default="formProps">
                    <div class="flex gap-4">
                      <BaseFormTextFieldWithValidator
                        v-model="emailCampaignNameInputValue"
                        v-bind="campaignNameFieldProps"
                        :disabled="formProps.isPending"
                      />
                      <BaseButton v-if="emailCampaignNameInputValue !== emailCampaign.name" success
                        >Save</BaseButton
                      >
                    </div>
                  </template>
                </BaseVeeForm>
                <h1 v-else class="text-sm">Edit Email Campaign</h1>
              </div>

              <div class="flex-none">
                <button
                  type="button"
                  class="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  @click="modalProps.close"
                >
                  <span class="sr-only">Close</span>
                  <svg
                    class="inline-flex h-6 w-6"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          <div class="px-4 sm:px-6 py-4 border-b bg-white flex-none">
            <div>
              <div class="sm:hidden">
                <label for="tabs" class="sr-only">Select a tab</label>
                <!-- Use an "onChange" listener to redirect the user to the selected tab URL. -->
                <select
                  v-model="currentTab"
                  id="tabs"
                  name="tabs"
                  class="block w-full focus:ring-indigo-500 focus:border-indigo-500 border-gray-300 rounded-md"
                >
                  <option v-for="option in tabOptions" :key="option.value" :value="option.value">
                    {{ option.label }}
                  </option>
                </select>
              </div>
              <div class="hidden sm:block">
                <nav class="flex space-x-4" aria-label="Tabs">
                  <!-- Current: "bg-gray-100 text-gray-700", Default: "text-gray-500 hover:text-gray-700" -->
                  <a
                    v-for="option in tabOptions"
                    :key="option.value"
                    role="button"
                    :class="[
                      currentTab === option.value
                        ? 'bg-gray-100 text-gray-700 px-3 py-2 font-medium text-sm rounded-md'
                        : 'text-gray-500 hover:text-gray-700 px-3 py-2 font-medium text-sm rounded-md',
                    ]"
                    @click.prevent="currentTab = option.value"
                  >
                    {{ option.label }}
                  </a>
                </nav>
              </div>
            </div>
          </div>
          <div class="flex-1 bg-white overflow-auto">
            <component
              v-if="emailCampaign"
              :is="currentComponent"
              :email-campaign="emailCampaign"
              :can-update="canUpdate"
              :on-update="updateEmailCampaign"
              @close-modal="modalProps.close()"
            />
          </div>
        </div>
      </div>
    </template>
  </VModal>
</template>

<script>
import { emailCampaignsClient } from '@/api/clients';

export default {
  name: 'EditEmailCampaignDialog',
  props: {
    emailCampaignId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      emailCampaign: null,
      emailCampaignNameInputValue: '',
      tabOptions: {
        design: {
          label: 'Design',
          value: 'design',
          component: () => import('./DesignTab.vue'),
        },
        setup: {
          label: 'Setup',
          value: 'setup',
          component: () => import('./SetupTab.vue'),
        },
        target: {
          label: 'Target',
          value: 'target',
          component: () => import('./TargetTab.vue'),
        },
        launch: {
          label: 'Launch',
          value: 'launch',
          component: () => import('./LaunchTab.vue'),
        },
      },
      currentTab: 'design',
    };
  },
  computed: {
    campaignNameFieldProps() {
      return {
        validator: {
          name: 'name',
          vid: 'name',
          rules: { required: true },
        },
        field: {
          id: 'campaing_name',
          inputClasses: 'max-w-lg bg-white',
        },
      };
    },
    currentComponent() {
      return this.tabOptions[this.currentTab]?.component ?? this.tabOptions.design.component;
    },
    canUpdate() {
      if (!this.emailCampaign) {
        return false;
      }

      return ['DRAFT', 'CANCELLED', 'FAILED'].indexOf(this.emailCampaign.status) >= 0;
    },
  },
  created() {
    this.fetchEmailCampaign();
  },
  methods: {
    fetchEmailCampaign() {
      return emailCampaignsClient.read(this.emailCampaignId).then((response) => {
        this.emailCampaign = response.data;
        this.emailCampaignNameInputValue = response.data?.name || '';
      });
    },
    updateEmailCampaign(data) {
      return emailCampaignsClient.update(this.emailCampaignId, data).then(() => {
        this.$router.push({
          name: this.$route.name,
          params: this.$route.params,
          query: {
            ...this.$route.query,
            email_campaign_updated: new Date().getTime(),
          },
        });
        this.fetchEmailCampaign();
      });
    },
    updateEmailCampaignName() {
      return this.updateEmailCampaign({
        name: this.emailCampaignNameInputValue,
      });
    },
  },
};
</script>
